import React from 'react';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { MARKETING_EMAIL_PROVIDER_OPTIONS } from '../model/marketing-email-providers.model.js';
import {
  MarketingEmailMultiSelect,
  MARKETING_EMAIL_LANGUAGE_OPTIONS,
  MARKETING_EMAIL_PROFILE_STATUS_OPTIONS,
} from './marketing-email-audience-filters.jsx';

function MarketingEmailProvidersPageView({
  connectionRows,
  jobRows,
  selectedUid,
  form,
  countryOptions = [],
  filters,
  targetListId,
  dryRun,
  isLoading,
  isSaving,
  isTesting,
  isSyncing,
  error,
  success,
  formErrors,
  onFormChange,
  onFilterChange,
  setTargetListId,
  selectConnection,
  startNewConnection,
  saveConnection,
  testConnection,
  runDryRun,
  startSyncJob,
  reload,
}) {
  const isBusy = isSaving || isTesting || isSyncing;

  return (
    <div>
      <FloatingAlerts info={isBusy ? 'Processing provider sync request...' : ''} error={error} success={success} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>Email provider sync</h2>
          <p>Connect external campaign providers and sync eligible CRM users as contacts.</p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={reload} disabled={isLoading || isBusy}>Refresh</button>
          <button type="button" className="btn btn-primary" onClick={startNewConnection} disabled={isBusy}>
            <i className="ti ti-plus me-1"></i>New connection
          </button>
        </div>
      </div>

      <LoadingContainer isLoading={isLoading} hasData>
        <div className="row g-3">
          <div className="col-xl-5">
            <div className="card card-body mb-3">
              <h5 className="mb-3">Connections</h5>
              <div className="list-group">
                {connectionRows.length === 0 ? <div className="text-muted">No provider connections yet.</div> : null}
                {connectionRows.map((row) => (
                  <button
                    type="button"
                    key={row.key}
                    className={`list-group-item list-group-item-action ${selectedUid === row.uid ? 'active' : ''}`}
                    onClick={() => selectConnection(row.uid)}
                  >
                    <div className="d-flex justify-content-between gap-2">
                      <span className="fw-semibold">{row.displayName}</span>
                      <span>{row.providerLabel}</span>
                    </div>
                    <div className="small">{row.statusLabel} · {row.configuredLabel}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card card-body">
              <h5 className="mb-3">Sync jobs</h5>
              <div className="table-responsive">
                <table className="table table-sm align-middle text-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Status</th>
                      <th>Result</th>
                      <th>Failed</th>
                      <th>Skipped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobRows.length === 0 ? <tr><td colSpan="5" className="text-muted">No sync jobs found.</td></tr> : null}
                    {jobRows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.providerLabel}</td>
                        <td>{row.statusLabel}</td>
                        <td>{row.resultLabel}</td>
                        <td>{row.failedCount}</td>
                        <td>{row.skippedCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-xl-7">
            <div className="card card-body mb-3">
              <h5 className="mb-3">Connection settings</h5>
              <form onSubmit={saveConnection}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Provider</label>
                    <select
                      className="form-select"
                      value={form.provider}
                      onChange={(event) => onFormChange('provider', event.target.value)}
                      disabled={isBusy}
                    >
                      {MARKETING_EMAIL_PROVIDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Display name</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.displayName ? 'is-invalid' : ''}`}
                      value={form.displayName}
                      onChange={(event) => onFormChange('displayName', event.target.value)}
                      disabled={isBusy}
                      required
                    />
                    {formErrors.displayName ? <div className="invalid-feedback">{formErrors.displayName}</div> : null}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">API key</label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.apiKey}
                      onChange={(event) => onFormChange('apiKey', event.target.value)}
                      disabled={isBusy}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">API base URL</label>
                    <input
                      type="url"
                      className="form-control"
                      value={form.apiBaseUrl}
                      onChange={(event) => onFormChange('apiBaseUrl', event.target.value)}
                      disabled={isBusy}
                      placeholder="Provider default"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Default list ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.defaultListId}
                      onChange={(event) => onFormChange('defaultListId', event.target.value)}
                      disabled={isBusy}
                    />
                  </div>
                  <div className="col-md-6 d-flex align-items-end">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="marketing-provider-enabled"
                        checked={form.enabled}
                        onChange={(event) => onFormChange('enabled', event.target.checked)}
                        disabled={isBusy}
                      />
                      <label className="form-check-label" htmlFor="marketing-provider-enabled">Enabled</label>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap justify-content-end gap-2 mt-4">
                  <button type="submit" className="btn btn-primary" disabled={isBusy}>{isSaving ? 'Saving...' : 'Save connection'}</button>
                  <button type="button" className="btn btn-outline-secondary" onClick={testConnection} disabled={isBusy || !selectedUid}>
                    {isTesting ? 'Testing...' : 'Test connection'}
                  </button>
                </div>
              </form>
            </div>

            <div className="card card-body">
              <h5 className="mb-3">Contact sync</h5>
              <div className="row g-3">
                <MarketingEmailMultiSelect
                  name="countries"
                  label="Countries"
                  value={filters.countries}
                  options={countryOptions}
                  onChange={onFilterChange}
                  disabled={isBusy}
                />
                <MarketingEmailMultiSelect
                  name="profileStatuses"
                  label="Profile statuses"
                  value={filters.profileStatuses}
                  options={MARKETING_EMAIL_PROFILE_STATUS_OPTIONS}
                  onChange={onFilterChange}
                  disabled={isBusy}
                />
                <MarketingEmailMultiSelect
                  name="languages"
                  label="Languages"
                  value={filters.languages}
                  options={MARKETING_EMAIL_LANGUAGE_OPTIONS}
                  onChange={onFilterChange}
                  disabled={isBusy}
                />
                <div className="col-md-6">
                  <label className="form-label">Target list ID</label>
                  <input type="text" className="form-control" value={targetListId} onChange={(event) => setTargetListId(event.target.value)} placeholder="Connection default" />
                </div>
                <div className="col-md-6 d-flex align-items-end justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={runDryRun} disabled={isBusy}>
                    Dry run
                  </button>
                  <button type="button" className="btn btn-success" onClick={startSyncJob} disabled={isBusy || !selectedUid}>
                    Start sync
                  </button>
                </div>
              </div>
              {dryRun ? (
                <div className="alert alert-info mt-3 mb-0">
                  Selected contacts: {dryRun.selectedCount ?? dryRun.selected ?? 0}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </LoadingContainer>
    </div>
  );
}

export default MarketingEmailProvidersPageView;
